import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './AuthContext'

const SubscriptionContext = createContext(null)

const FREE_WARRANTY_LIMIT = 10

export function SubscriptionProvider({ children }) {
  const { user } = useAuth()
  const [plan, setPlan] = useState('free')
  const [subscription, setSubscription] = useState(null)
  const [warrantyCount, setWarrantyCount] = useState(0)
  const [loading, setLoading] = useState(true)

  const refreshSubscription = useCallback(async () => {
    if (!user) {
      setPlan('free')
      setSubscription(null)
      setWarrantyCount(0)
      setLoading(false)
      return
    }

    try {
      const [
        { data: profile },
        { data: activeSub },
        { data: warrantiesData }
      ] = await Promise.all([
        supabase.from('profiles').select('plan').eq('id', user.id).single(),
        supabase.from('subscriptions').select('*').eq('user_id', user.id).eq('status', 'active').order('end_date', { ascending: false }).limit(1).maybeSingle(),
        supabase.from('warranties').select('id').eq('user_id', user.id),
      ])

      setPlan(profile?.plan || 'free')
      setSubscription(activeSub || null)
      setWarrantyCount(warrantiesData?.length || 0)
    } catch {
      // If subscriptions table doesn't exist yet or query fails, default to free
      setPlan('free')
      setSubscription(null)
    }
    setLoading(false)
  }, [user])

  useEffect(() => {
    refreshSubscription()
  }, [refreshSubscription])

  const canAddWarranty = plan === 'premium' || warrantyCount < FREE_WARRANTY_LIMIT

  return (
    <SubscriptionContext.Provider value={{
      plan,
      subscription,
      warrantyCount,
      canAddWarranty,
      freeLimit: FREE_WARRANTY_LIMIT,
      loading,
      refreshSubscription,
    }}>
      {children}
    </SubscriptionContext.Provider>
  )
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext)
  if (!ctx) throw new Error('useSubscription must be used inside <SubscriptionProvider>')
  return ctx
}
